import { useEffect, useState } from "react";
import { ShotFade, useGuided } from "@heroncendre/player";
import { VariantSwitcher } from "../VariantSwitcher.js";
import { useReducedMotion } from "../useReducedMotion.js";
import { PAS_CLAVIER_DEG } from "../FlechesRotation.js";
import { actionClavier, bandesVisibles, barreFlottante } from "./modeGuide.js";
import { useGuide, type OptionsGuide } from "./useGuide.js";
import { GuideLetterbox } from "./GuideLetterbox.js";
import { BandeBasse, BandeBasseFin, BandeHaute } from "./BandesContenu.js";
import { AnnonceMain, BoutonChapitres, GuideFloatingBar, QuitterFlottant } from "./GuideFloatingBar.js";
import { ChapterEnd } from "./ChapterEnd.js";
import { ChapterMenu } from "./ChapterMenu.js";
import { BANDE } from "./guideStyles.js";

/**
 * ============================================================================
 *  GuideInterface.tsx — L'interface de la visite guidée (thème Écrin)
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_visite_guidee/`. Remplace l'interface
 *  du moteur (`GuidedExperience` : bandes cinéma, pilule de chapitres, vignettes de
 *  fin, compte à rebours de reprise), qui n'est plus montée.
 *
 *  Trois états, lus dans la machine du moteur (`modeGuide`) :
 *   - GUIDE : bandes noires, la caméra suit la séquence ;
 *   - MAIN  : le visiteur a pris la caméra, les bandes rentrent, la voix continue ;
 *   - FIN   : fin de chapitre, les choix pour la suite.
 *
 *  ── Coexistence ────────────────────────────────────────────────────────────
 *
 *  `SceneNav` et `SceneTitle` ne sont pas montés pendant le parcours (voir `Libre`
 *  dans `App.tsx`) et reviennent à la sortie ; le contrôleur d'ambiances
 *  (`VariantSwitcher`) n'est monté qu'en mode main. Le guide a sa propre navigation :
 *  la barre segmentée.
 *
 *  ── Clavier ────────────────────────────────────────────────────────────────
 *
 *  Espace : lecture / pause. ← → : prendre la main et tourner. Échap : reprendre le
 *  guide (en main) ou revenir au chapitre (en exploration). Voir `actionClavier`.
 * ==========================================================================*/

const ETROIT = "(max-width: 720px)";

function useEtroit(): boolean {
  const lire = () => typeof window.matchMedia === "function" && window.matchMedia(ETROIT).matches;
  const [etroit, setEtroit] = useState(lire);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(ETROIT);
    const surChangement = () => setEtroit(media.matches);
    media.addEventListener("change", surChangement);
    return () => media.removeEventListener("change", surChangement);
  }, []);
  return etroit;
}

export function GuideInterface({ guideId, surSortie }: OptionsGuide) {
  const guide = useGuide({ guideId, surSortie });
  const etroit = useEtroit();
  const reduit = useReducedMotion();
  const { mode, actions } = guide;
  const menuOuvert = guide.menu.ouvert;
  const { assetBaseUrl } = useGuided();

  useEffect(() => {
    if (mode === null) return;
    const surTouche = (event: KeyboardEvent) => {
      // Le menu des chapitres est modal : il garde le clavier (Échap le ferme, voir `ChapterMenu`).
      if (menuOuvert) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // Les flèches se répètent (on tourne en les maintenant) ; Espace et Échap, non.
      if (event.repeat && event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      const cible = event.target as HTMLElement | null;
      const surControle = !!cible && ["BUTTON", "A", "INPUT", "TEXTAREA", "SELECT"].includes(cible.tagName);
      const action = actionClavier(mode, event.key, surControle);
      if (action === null) return;
      event.preventDefault();
      if (action === "lecture") actions.basculerLecture();
      else if (action === "tourner-gauche") actions.tourner(-PAS_CLAVIER_DEG);
      else if (action === "tourner-droite") actions.tourner(PAS_CLAVIER_DEG);
      else if (action === "reprendre") actions.reprendre();
      else if (action === "retour-chapitre") actions.retourChapitre();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [mode, actions, menuOuvert]);

  if (mode === null) return null;

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 20 }}>
      {/* Les fondus au noir ENTRE les plans (`settings.guidedSceneTransition`, `fadeIn` / `fadeOut`
          des plans) : une couche noire que le moteur anime au fil de la voix. Elle faisait partie de
          l'interface du moteur qu'on ne monte plus ; sans elle, les scènes se coupent sec. Posée
          sous les bandes, sur l'image. */}
      <ShotFade />
      <GuideLetterbox
        visible={bandesVisibles(mode)}
        etroit={etroit}
        reduit={reduit}
        haut={<BandeHaute guide={guide} etroit={etroit} />}
        bas={mode === "fin" ? <BandeBasseFin guide={guide} etroit={etroit} /> : <BandeBasse guide={guide} etroit={etroit} />}
      />

      {mode === "fin" && <ChapterEnd guide={guide} etroit={etroit} />}

      {barreFlottante(mode) && (
        <>
          <QuitterFlottant guide={guide} etroit={etroit} />
          <BoutonChapitres guide={guide} etroit={etroit} />
          <GuideFloatingBar guide={guide} etroit={etroit} />
        </>
      )}
      <AnnonceMain guide={guide} etroit={etroit} />

      <ChapterMenu guide={guide} etroit={etroit} />

      {/* Le contrôleur d'ambiances n'existe qu'en main : en guide, ce sont les moments des
          plans qui conduisent la lumière. Posé au-dessus de la barre et des sous-titres,
          à l'aplomb de son bord gauche (variables lues par `variantSwitcher.css`). */}
      {barreFlottante(mode) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            ["--va-bas" as string]: etroit ? "calc(152px + env(safe-area-inset-bottom))" : "172px",
            ["--va-gauche" as string]: etroit ? "12px" : "max(12px, calc(50% - 310px))",
            ["--va-marge" as string]: etroit ? "12px" : "max(12px, calc(50% - 310px))",
          }}
        >
          <VariantSwitcher assetBaseUrl={assetBaseUrl} />
        </div>
      )}

      {/* Quitter, ou changer de chapitre : fondu vers le noir de la bande — la visite libre ou
          la scène du chapitre suivant ne se découvre qu'une fois installée. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: BANDE,
          opacity: guide.ui.sortie || guide.ui.fondu ? 1 : 0,
          transition: reduit ? "opacity .2s ease" : "opacity .5s ease",
          pointerEvents: guide.ui.sortie || guide.ui.fondu ? "auto" : "none",
          zIndex: 30,
        }}
      />
    </div>
  );
}
